import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TopBanner from "./TopBanner";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  styled 
} from "@mui/material";
import { useAuth } from '../utils/AuthContext';

const ConfirmCode = () => {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const { refreshUser } = useAuth();

  // Store timer reference to avoid resetting on re-render
  const timerRef = useRef();

  // Countdown timer for code expiration
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    timerRef.current = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timerRef.current);
  }, [timeLeft]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendTimeout <= 0) {
      setResendDisabled(false);
      return;
    }
    
    const timer = setTimeout(() => {
      setResendTimeout(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendTimeout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setConfirmationCode(value);
    setErrorMessage("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
  
    if (!confirmationCode || confirmationCode.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit code.");
      inputRef.current.focus();
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      const response = await axios.post(
        `${apiUrl}/auth/confirm`, 
        { code: confirmationCode },
        { 
          withCredentials: true, // REQUIRED for cookies
          headers: {
            'Content-Type': 'application/json' // Ensure proper content type
          }
        }
      );
  
      await refreshUser();
      
      navigate('/');
      
    } catch (error) {
      // Enhanced error handling
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          "Verification failed. Please try again.";
      setErrorMessage(errorMessage);
      inputRef.current.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const response = await axios.post(
        `${apiUrl}/auth/resend-code`, 
        {}, 
        { withCredentials: true }
      );
      
      setTimeLeft(300);
      setResendDisabled(true);
      setResendTimeout(response.data?.retry_after || 60);
      setSuccessMessage("New code sent successfully!");
      setErrorMessage("");
      setConfirmationCode("");
      inputRef.current.focus();
      
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.msg || "Failed to resend code. Please try again later."
      );
    }
  };

  // Styled components
  const CodeInput = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: theme.palette.background.paper,
      '& input': {
        textAlign: 'center',
        letterSpacing: '8px',
        fontSize: '24px',
        padding: '12px 16px',
      },
    },
  }));

  const SubmitButton = styled(Button)({
    borderRadius: '12px',
    height: '48px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
      color: '#a0a0a0',
    },
  });

  return (
    <Box sx={{ 
      paddingTop: "64px", 
      minHeight: "calc(100vh - 64px)", 
      backgroundColor: "#f5f7fa",
      display: "flex",
      flexDirection: "column",
    }}>
      <TopBanner />
      
      <Box sx={{
        maxWidth: "480px",
        width: "100%",
        margin: "32px auto",
        padding: { xs: "24px", sm: "40px" },
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            textAlign: "center", 
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
          }}
        >
          Verify Your Account
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: "center", 
            color: "text.secondary",
            mb: 4,
          }}
        >
          Enter the 6-digit code sent to your device
        </Typography>
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}
        
        <Box 
          component="form" 
          onSubmit={handleFormSubmit}
          sx={{ mb: 3 }}
        >
          <CodeInput
            inputRef={inputRef}
            fullWidth
            variant="outlined"
            placeholder="• • • • • •"
            value={confirmationCode}
            onChange={handleInputChange}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
            }}
            autoFocus
            sx={{ mb: 2 }}
          />
          
          <Typography 
            variant="caption" 
            sx={{ 
              display: "block", 
              textAlign: "center", 
              color: timeLeft < 60 ? "error.main" : "text.secondary",
              mb: 3,
            }}
          >
            {timeLeft > 0 
              ? `Code expires in ${formatTime(timeLeft)}` 
              : "Code has expired. Please request a new one."
            }
          </Typography>
          
          <SubmitButton
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting || timeLeft <= 0}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Verify Account"
            )}
          </SubmitButton>
        </Box>
        
        <Box sx={{ 
          textAlign: "center",
          borderTop: "1px solid #eee",
          pt: 3,
        }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Didn't receive a code?
          </Typography>
          
          <Button
            onClick={handleResendCode}
            disabled={resendDisabled || resendTimeout > 0}
            sx={{
              minWidth: "120px",
              textTransform: "none",
              fontWeight: 600,
              color: resendDisabled ? "text.disabled" : "primary.main",
            }}
          >
            {resendTimeout > 0 
              ? `Resend in ${resendTimeout}s` 
              : "Resend Code"
            }
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmCode;